/**
 * Color manipulation utilities for app settings
 */

/**
 * Converts hex color to RGB object
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    } : null;
}

/**
 * Converts RGB values to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
    // eslint-disable-next-line no-bitwise
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Lightens a hex color by a given amount (0-1)
 */
function lightenColor(hex: string, amount: number): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const { r, g, b } = rgb;

    // Lighten by mixing with white
    const newR = Math.round(r + (255 - r) * amount);
    const newG = Math.round(g + (255 - g) * amount);
    const newB = Math.round(b + (255 - b) * amount);

    return rgbToHex(
        Math.min(255, Math.max(0, newR)),
        Math.min(255, Math.max(0, newG)),
        Math.min(255, Math.max(0, newB)),
    );
}

/**
 * Darkens a hex color by a given amount (0-1)
 */
export function darkenColor(hex: string, amount: number): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const { r, g, b } = rgb;

    // Darken by mixing with black
    const newR = Math.round(r * (1 - amount));
    const newG = Math.round(g * (1 - amount));
    const newB = Math.round(b * (1 - amount));

    return rgbToHex(
        Math.min(255, Math.max(0, newR)),
        Math.min(255, Math.max(0, newG)),
        Math.min(255, Math.max(0, newB)),
    );
}

/**
 * App-level colors - used for web application UI
 */
interface AppColors {
    primaryColor: string; // Main brand color for headers, buttons, etc.
    secondaryColor: string; // Secondary brand color for accents
    textColor: string; // Main text color for UI elements
}

/**
 * Document-level colors - used for PDF generation
 */
export interface DocumentColors {
    documentPrimaryColor: string; // Main color for document headers, titles
    documentSecondaryColor: string; // Secondary color for document elements
    documentTextColor: string; // Main text color in documents
}

/**
 * Main colors interface for the color settings component
 */
interface MainColors {
    mainColor: string;
    secondaryColor: string;
    textColor: string;
}

/**
 * Detailed document colors - specific colors for various PDF elements
 */
export interface DetailedDocumentColors {
    // Colors that use document primary color
    header_text_color: string;
    chapter_cover_page_color: string;
    toc_chapter_title_color: string;
    toc_section_title_color: string;
    general_title_color: string;
    document_param_text_color: string;
    document_release_text_color: string;

    // Colors that use document text color variations
    footer_text_color: string;
    confidentiality_note_color: string;
    vendor_email_color: string;

    // Colors that use document secondary color
    vendor_section_color: string;
}

/**
 * Alias for DetailedDocumentColors to match component expectations
 */
type DetailedColors = DetailedDocumentColors;

/**
 * Automatically generates detailed document colors from main document colors
 */
export function generateDetailedDocumentColors(documentColors: DocumentColors): DetailedDocumentColors {
    const { documentPrimaryColor, documentSecondaryColor, documentTextColor } = documentColors;

    // Text color variations (lighter)
    const lightTextColor = lightenColor(documentTextColor, 0.2);

    return {
        // Document primary color group - use document primary color directly
        header_text_color: documentPrimaryColor,
        chapter_cover_page_color: documentPrimaryColor,
        toc_chapter_title_color: documentPrimaryColor,
        toc_section_title_color: documentPrimaryColor,
        general_title_color: documentPrimaryColor,
        document_param_text_color: documentPrimaryColor,
        document_release_text_color: documentPrimaryColor,

        // Document text color group - use document text color and lighter variant
        footer_text_color: lightTextColor,
        confidentiality_note_color: lightTextColor,
        vendor_email_color: documentTextColor,

        // Document secondary color usage
        vendor_section_color: documentSecondaryColor,
    };
}

/**
 * Generates detailed colors from main colors for the color settings component
 */
function generateDetailedColors(mainColors: MainColors): DetailedColors {
    const documentColors: DocumentColors = {
        documentPrimaryColor: mainColors.mainColor,
        documentSecondaryColor: mainColors.secondaryColor,
        documentTextColor: mainColors.textColor,
    };

    return generateDetailedDocumentColors(documentColors);
}

/**
 * Calculates the luminance of a color
 * Used to determine if a color is light or dark
 */
function getLuminance(hex: string): number {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;

    // Convert RGB to luminance using standard formula
    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map((c) => {
        const normalized = c / 255;
        return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determines if a color is light or dark based on its luminance
 * @param hex - The hex color to check
 * @param threshold - Luminance threshold (default: 0.5)
 * @returns true if the color is light, false if dark
 */
function isLightColor(hex: string, threshold = 0.5): boolean {
    return getLuminance(hex) > threshold;
}

/**
 * Gets the appropriate text color (black or white) for the given background color
 * to ensure good contrast and readability
 * @param backgroundColor - The background color in hex format
 * @param lightColor - Color to use for dark backgrounds (default: '#ffffff')
 * @param darkColor - Color to use for light backgrounds (default: '#000000')
 * @returns The appropriate text color for maximum contrast
 */
export function getContrastTextColor(
    backgroundColor: string,
    lightColor = '#ffffff',
    darkColor = '#000000',
): string {
    return isLightColor(backgroundColor) ? darkColor : lightColor;
}

/**
 * Calculates the contrast ratio between two colors
 * Useful for accessibility compliance (WCAG guidelines)
 * @param color1 - First color in hex format
 * @param color2 - Second color in hex format
 * @returns Contrast ratio (1-21, where 21 is maximum contrast)
 */
function getContrastRatio(color1: string, color2: string): number {
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    
    return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Extracts color from CSS custom property or returns the color as-is
 * Handles both CSS variables and direct hex colors
 */
export function extractColorValue(color: string): string {
    if (color.startsWith('var(')) {
        // Extract CSS variable name and get computed value
        const varName = color.match(/var\(([^,)]+)/)?.[1];
        if (varName && typeof window !== 'undefined') {
            const computedValue = getComputedStyle(document.documentElement)
                .getPropertyValue(varName)
                .trim();
            return computedValue || color;
        }
    }
    return color;
}

/**
 * Updates the CSS custom properties for automatic text and icon colors
 * This affects button text colors and modal header icon colors
 * Call this whenever the primary color changes to ensure proper contrast
 */
export function updatePrimaryButtonTextColor(primaryColor?: string): void {
    if (typeof window === 'undefined') return;
    
    // Get the primary color from CSS variable if not provided
    const colorToUse = primaryColor || getComputedStyle(document.documentElement)
        .getPropertyValue('--marriott-primary')
        .trim() || '#00a4ba';
    
    // Calculate the appropriate text color
    const textColor = getContrastTextColor(colorToUse);
    
    // Debug logging
    console.log('🎨 Auto Color Update:', {
        primaryColor: colorToUse,
        calculatedTextColor: textColor,
        isLightColor: isLightColor(colorToUse),
        luminance: getLuminance(colorToUse),
    });
    
    // Set the CSS custom properties
    document.documentElement.style.setProperty('--auto-primary-text-color', textColor);
    document.documentElement.style.setProperty('--auto-modal-icon-color', textColor);
    
    // Also log the current CSS variable values to verify they're set
    const currentTextColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--auto-primary-text-color');
    const currentIconColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--auto-modal-icon-color');
    console.log('✅ CSS Variables Set:', {
        '--auto-primary-text-color': currentTextColor,
        '--auto-modal-icon-color': currentIconColor,
    });
}

/**
 * Initializes automatic colors for UI elements based on current CSS variables
 * This includes button text colors and modal icon colors
 * Call this on app startup and whenever color settings change
 */
export function initializeAutoButtonColors(): void {
    updatePrimaryButtonTextColor();
}
